// TEMPORARY inline copies of helpers from the app's `@/util`.
// These exist so `@shared/theme` has no dependency on app code. They should be
// replaced by imports from a future `@shared/util` package once it is extracted.
import { useState } from 'react';

declare global {
  interface Window {
    SAM?: Record<string, unknown>;
    _env?: Record<string, unknown>;
  }
}

const hasDebugParam = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const params = new URLSearchParams(window.location.search);
  return params.get('debug') === 'true';
};

const ensureDebugStore = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  if (!window.SAM) {
    window.SAM = { name: 'SAM Debug Assistance' };
  }

  return window.SAM;
};

export function attachToDebug(name: string, value: unknown) {
  if (!hasDebugParam()) {
    return;
  }

  const store = ensureDebugStore();
  if (!store) {
    return;
  }

  store[name] = value;
}

export function useLocalStorage<T>(
  key: string | undefined,
  defaultValue: T
): [T, (val: T) => void] {
  const disableStorage = key === undefined;

  const [storedValue, setStoredValue] = useState<T>(() => {
    if (disableStorage) {
      return defaultValue;
    }

    try {
      const item = window.localStorage.getItem(key);
      if (!item) {
        return defaultValue;
      }
      return JSON.parse(item);
    } catch (error) {
      console.log(error);
      return defaultValue;
    }
  });

  const setValue = (value: T | ((val: T) => T)) => {
    try {
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);

      if (disableStorage) {
        return;
      }

      if (valueToStore === undefined) {
        window.localStorage.removeItem(key);
      } else {
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      }
    } catch (error) {
      console.log(error);
    }
  };

  return [storedValue, setValue];
}
